import type { CustReqItem } from './types'

// 고객 요청(S16) 카드 표시 판정 — 순수 함수(시험: custReq.logic.test.ts).
// 상태 이름·받은 경로 이름은 서버가 내려주는 statusName·srcName 을 그대로 쓴다(여기서 하드코딩하지 않음).
// 이번 범위에서 [반장님께 추가 전달]·검수 이동 버튼은 그리지 않는다(값 해석만).

/** 상태 코드(공통코드 ZEU_CUST_REQ_STATUS) 값 문자열 */
export const CUST_REQ_STATUS = {
  RECEIVED: 'RECEIVED',
  DELIVERED: 'DELIVERED',
  APPLIED: 'APPLIED',
  CHECKED: 'CHECKED',
} as const

/** 받은 경로 기본값(기획: 카톡이 기본) */
export const CUST_REQ_DEFAULT_SRC = 'KAKAO'
/** 요청당 사진 최대 장수 */
export const CUST_REQ_MAX_PHOTOS = 5
/** 요청 문장 최대 길이(서버 검증과 같은 값) */
export const CUST_REQ_CONTENT_MAX = 1000

export const CUST_REQ_MESSAGE = {
  title: '고객 요청',
  add: '+ 추가',
  empty: '등록된 고객 요청이 없습니다',
  emptyHint: '고객이 카톡·전화·현장에서 요청한 내용을 [+ 추가]로 등록해 주세요.',
  loadError: '고객 요청을 불러오지 못했습니다.',
  srcLoadError: '받은 경로를 불러오지 못했습니다.',
  contentLabel: '요청 내용',
  contentPlaceholder: '카톡 문장을 그대로 붙여넣어 주세요',
  contentRequired: '요청 내용을 입력해 주세요',
  contentTooLong: `요청 내용은 ${CUST_REQ_CONTENT_MAX}자 이하로 입력해 주세요`,
  srcLabel: '받은 경로',
  photoSelect: '+ 사진 선택',
  photoDrop: '사진을 끌어다 놓거나 붙여넣어 주세요',
  photoHint: `이미지 최대 ${CUST_REQ_MAX_PHOTOS}장`,
  photoRemove: '사진 삭제',
  photoOverflow: (n: number) => `사진은 ${CUST_REQ_MAX_PHOTOS}장까지 첨부할 수 있어 ${n}장은 제외했습니다.`,
  photoNotImage: '이미지 파일만 첨부할 수 있어 일부 파일은 제외했습니다.',
  save: '저장',
  saving: '저장 중...',
  cancel: '취소',
  edit: '수정',
  remove: '삭제',
  removeConfirmTitle: '고객 요청 삭제',
  removeConfirmBody: '이 고객 요청을 삭제할까요? 삭제하면 되돌릴 수 없습니다.',
  removeConfirmOk: '삭제',
  unsent: '미전달',
  unsentTitle: '작업요청을 보낸 뒤 접수되어 아직 반장님께 전달되지 않은 요청입니다',
  appliedNote: '검수는 작업보고확인에서 진행합니다',
  original: '원본 보기',
  originalFail: '원본 사진을 불러오지 못했습니다.',
  checkedToggle: (n: number) => `확인 완료 ${n}건`,
  saveFail: '고객 요청을 저장하지 못했습니다.',
  deleteFail: '고객 요청을 삭제하지 못했습니다.',
  saved: '고객 요청을 등록했습니다.',
  updated: '고객 요청을 수정했습니다.',
  deleted: '고객 요청을 삭제했습니다.',
  addDelivery: '반장님께 추가 전달',
  addDeliveryTitle: '반장님께 추가 전달',
  addDeliveryCount: (n: number) => `미전달 요청 ${n}건을 반장님께 전달합니다.`,
  addDeliveryTo: (name: string) => `받는 반장님: ${name}`,
  addDeliveryToUnknown: '받는 반장님: 이 공정의 작업요청 수신 작업자',
  addDeliveryNote: '전달되면 요청은 전달됨으로 바뀌고, 반장님께 알림톡이 발송됩니다.',
  addDeliveryOk: '전달',
  addDeliveryDone: (n: number) => `고객 요청 ${n}건을 반장님께 전달했습니다.`,
  addDeliveryFail: '추가 전달을 보내지 못했습니다. 요청은 전달되지 않은 상태로 남아 있습니다.',
  addDeliveryConflict: '요청이 그 사이 변경됐거나 전달 대상이 없습니다. 목록을 새로 불러왔으니 확인 후 다시 시도해 주세요.',
} as const

/** 목록 줄 오른쪽의 사진 수 표기 — 0장이면 빈 문자열 */
export function photoCountText(n: number): string {
  return n > 0 ? `(사진 ${n})` : ''
}

/** 진행 중 요청(접수·전달됨·반영됨)과 확인 완료 요청을 나눈다. 순서는 서버가 준 접수 순을 유지 */
export function splitByChecked(items: CustReqItem[]): { active: CustReqItem[]; checked: CustReqItem[] } {
  const active: CustReqItem[] = []
  const checked: CustReqItem[] = []
  for (const it of items) (it.statusCode === CUST_REQ_STATUS.CHECKED ? checked : active).push(it)
  return { active, checked }
}

export interface CustReqRowView {
  /** [수정][삭제] 노출 — 접수(서버 editable)일 때만 */
  showEditActions: boolean
  /** 미전달 주황 표시 */
  showUnsent: boolean
  /** 반영됨 안내 문구 노출 */
  showAppliedNote: boolean
}

/** 요청 한 줄의 상태별 표시 판정 */
export function getRowView(item: CustReqItem): CustReqRowView {
  const received = item.statusCode === CUST_REQ_STATUS.RECEIVED
  return {
    showEditActions: item.editable && received,
    showUnsent: item.unsent && received,
    showAppliedNote: item.statusCode === CUST_REQ_STATUS.APPLIED,
  }
}

/** 상태 뱃지 색(ui-core Badge status) — 주황(warning)은 미전달 표시 전용 */
export function statusBadgeTone(statusCode: string): 'info' | 'done' | 'success' | 'mute' {
  switch (statusCode) {
    case CUST_REQ_STATUS.RECEIVED: return 'info'
    case CUST_REQ_STATUS.DELIVERED: return 'done'
    case CUST_REQ_STATUS.APPLIED: return 'success'
    default: return 'mute'
  }
}

/** 접수 시각(YYYY-MM-DDTHH:mm:ss) → `M/D HH:mm`. 형식이 다르면 빈 문자열(값을 지어내지 않음) */
export function formatReceivedAt(receivedAt: string | null | undefined): string {
  const m = /^\d{4}-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(receivedAt ?? '')
  return m ? `${Number(m[1])}/${Number(m[2])} ${m[3]}` : ''
}

/** 요청 문장 검증 — 오류 문구 또는 null */
export function validateContent(content: string): string | null {
  const t = content.trim()
  if (t === '') return CUST_REQ_MESSAGE.contentRequired
  if (t.length > CUST_REQ_CONTENT_MAX) return CUST_REQ_MESSAGE.contentTooLong
  return null
}

export interface MergePhotosResult {
  /** 합친 결과(최대 maxTotal 장) */
  next: File[]
  /** 장수 초과로 제외된 수 */
  overflow: number
  /** 이미지가 아니라 제외된 수 */
  notImage: number
}

const isImage = (f: File) => f.type.startsWith('image/')

/**
 * 사진 추가 — 이미지가 아닌 파일은 제외하고, 기존 + 추가가 maxTotal 을 넘으면 넘는 분량을 제외한다.
 * existingCount: 이미 저장된(서버) 사진 수 등 같은 한도에 포함되는 장수
 */
export function mergePhotoFiles(
  current: File[], incoming: File[], existingCount = 0, maxTotal = CUST_REQ_MAX_PHOTOS,
): MergePhotosResult {
  const images = incoming.filter(isImage)
  const notImage = incoming.length - images.length
  const room = Math.max(0, maxTotal - existingCount - current.length)
  const accepted = images.slice(0, room)
  return { next: [...current, ...accepted], overflow: images.length - accepted.length, notImage }
}

/** 붙여넣기·끌어놓기에서 받은 파일 목록 중 이미지 파일만 */
export function pickImageFiles(files: ArrayLike<File> | null | undefined): File[] {
  return files ? Array.from(files).filter(isImage) : []
}

/** 등록 시도 1회에 쓰는 중복 방지 키(UUID 36자) */
export function newRequestKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const h = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')
  return `${h()}${h()}-${h()}-4${h().slice(1)}-a${h().slice(1)}-${h()}${h()}${h()}`
}

/** 추가 전달 대상 — 미전달(접수이면서 서버가 미전달로 판정한) 요청. 판정은 서버 unsent 값 그대로 쓴다 */
export function pickUnsentItems(items: CustReqItem[]): CustReqItem[] {
  return items.filter(it => getRowView(it).showUnsent)
}
